import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon } from "../../atoms";
import { useTranslation } from "react-i18next";

export interface IBoxOptions {
  key: any;
  value: any;
  logo?: string;
  extraText?: string;
}
interface IBase {
  bgColor?: string;
  lblColor?: string;
}
interface SelectBoxProps extends IBase {
  boxOptions: IBoxOptions[];
  onChange: (item: IBoxOptions) => void;
  defaultKey: string;
  isWithdraw?: boolean;
  isFilter?: boolean;
  simpleText?: boolean;
  forceDefaultKey?: boolean;
}

/**
 * Select Box Wrapper
 */
const SelectBoxWrapper = styled.div`
  position: relative;
  border: solid 1px ${(theme: any) => theme.theme["Light5"]};
  border-radius: 8px;
  min-width: 150px;
`;
/**
 * Select Box Wrapper Style
 */
const StyledSelectBox = styled.div<IBase>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  border-radius: 8px;
  position: relative;
  padding: 0 10px;
  background-color: ${(p) => (theme) => theme.theme[p.bgColor || "Light6"]};
`;
/**
 * Select Box Label
 */
const SelectLabel = styled.span<IBase>`
  color: ${(p) => (theme) => theme.theme[p.bgColor || "Light2"]};
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 100%;
`;
const OptionMenus = styled.div<IBase>`
  border-radius: 8px;
  position: absolute;
  height: 0;
  transition: height 0.3s ease-in 150ms;
  overflow: hidden;
  top: 45px;
  width: 100%;
  background-color: ${(theme) => theme.theme.Light6};
  &.active {
    height: auto;
    overflow: visible;
  }
  z-index: 1;
  ul {
    padding: 0;
    margin: 0;
    list-style: none;
    li {
      padding: 0 1rem;
      height: 44px;
      display: flex;
      justify-content: space-between;
      flex-direction: row-reverse;
      align-items: center;
      // text-align: right;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      color: ${(theme) => theme.theme.Light1};

      cursor: pointer;
      &:last-child {
        border-bottom: none;
      }
      &:hover {
        background-color: ${(theme) => theme.theme.Light5};
      }
      .bank-logo {
        width: 50%;
        //height: 24px;
        img {
          width: 24px;
          height: 24px;
          object-fit: contain;
        }
      }
      &.simple-list {
        justify-content: flex-end;
        background: ${(theme) => theme.theme.bgColor1};
      }
      .card-number {
        direction: ltr;
      }
      .simple-text {
        text-align: right;
        direction: rtl;
      }

      &:not(:last-child) {
        border-bottom: 1px solid ${(theme) => theme.theme.Light5};
      }
    }
  }
`;
/**
 * Main Ui Component
 * @param bgColor
 * @param lblColor
 * @param boxOptions
 * @param onChange
 * @param defaultKey
 * @param isWithdraw
 * @param isFilter
 * @param simpleText
 * @param forceDefaultKey
 * @constructor
 */
export const SelectBox: React.FC<SelectBoxProps> = ({
  bgColor = "Light6",
  lblColor = "Light1",
  boxOptions = [],
  onChange,
  defaultKey,
  isWithdraw = false,
  isFilter = false,
  simpleText = false,
  forceDefaultKey = false,
}) => {
  /**
   * Actvie States
   */
  const [active, setActive] = useState("");
  const [selected, setSelected] = useState<IBoxOptions>(boxOptions[0]);
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  /**
   * Close Menu when clicked outside
   */
  useEffect(() => {
    document.addEventListener("click", () => {
      setActive("");
    });
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("click", () => {
        setActive("");
      });
      setSelected(boxOptions[0]);
      setActive("");
    };
  }, [window]);
  /**
   * Use Default Key Detector
   */
  useEffect(() => {
    if (forceDefaultKey) {
      // do nothing
    } else if (isWithdraw) {
      setSelected({
        ...selected,
        value: defaultKey ? defaultKey : t("withdraw.deposit.selectPlaceholder"),
      });
    } else if (isFilter) {
      setSelected({
        ...selected,
        value: t("transactionTable.categories.all"),
      });
    } else {
      setSelected({
        ...selected,
        value: `${defaultKey} در قیمت مشخص`,
      });
    }
  }, [defaultKey]);
  /**
   * On Drop Down Clicked
   */
  const toggleMenu = (e?: any) => {
    if (e) {
      e.stopPropagation();
    }
    const status = active === "active" ? "" : "active";
    setActive(status);
  };
  /**
   * Change Selected Item
   * @param item<IBoxOptions>
   */
  const onSelect = (item: IBoxOptions) => {
    setSelected(item);
    onChange(item);
    toggleMenu();
  };
  /**
   * Render Card Number
   * To split 4 digit
   */
  const renderCardNumber = (cardNumber: any) => {
    if (cardNumber !== null && typeof cardNumber === "string") {
      const temp = cardNumber.match(/.{1,4}/g);

      if (temp) {
        return temp.join(" ");
      }
    }

    return cardNumber;
  };
  /**
   * Base Render View
   */
  return (
    <SelectBoxWrapper className={"select-box-wrapper"}>
      <StyledSelectBox
        bgColor={bgColor}
        onClick={toggleMenu}
        className={"select-box-input"}
      >
        <SelectLabel lblColor={lblColor}>{selected.value}</SelectLabel>
        <Icon name="arrowdown" width="12px" height="12px" />
      </StyledSelectBox>
      <OptionMenus className={`${active} select-box-options`}>
        <ul>
          {boxOptions &&
            boxOptions.map((item, index) => (
              <li
                style={{ textAlign: isRtl ? 'right' : 'left' }}
                className={simpleText ? "simple-list" : "bank-list"}
                onClick={() => onSelect(item)}
                key={index}
              >
                {simpleText ? (
                  <span className="simple-text">{item.value}</span>
                ) : (
                  <span className="card-number">
                    {renderCardNumber(item.value)}
                  </span>
                )}

                {item.logo && (
                  <div className="bank-logo">
                    <img src={item.logo} />
                    {item.extraText && <span> {t("deposit.addCardDialog.bank")} {item.extraText}</span>}
                  </div>
                )}
              </li>
            ))}
        </ul>
      </OptionMenus>
    </SelectBoxWrapper>
  );
};
